import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, ArrowLeft, Check, ChevronDown, ChevronUp, Copy, Eye, GitCompareArrows, History, Minus, Plus, RefreshCw, Search, Sparkles, FileDown, FileText, GitCompare } from 'lucide-react';
import ExpandableDescription from './ExpandableDescription';
import SingleSelectDropdown from './SingleSelectDropdown';
import { buildDocumentSharePath } from '../utils/documentLinks';

const LOADING_STEPS = [
  {
    title: 'Baixando PDFs',
    description: 'Baixando as versões nova e antiga diretamente do portal fiscal.'
  },
  {
    title: 'Convertendo PDFs em Texto',
    description: 'Extraindo o texto integral dos PDFs e limpando ruídos, paginação e sumários.'
  },
  {
    title: 'Gerando Diferenças',
    description: 'Calculando linhas adicionadas e removidas em formato intercalado.'
  },
  {
    title: 'Analisando Diferenças',
    description: 'Organizando alterações estruturais, termos técnicos e regras afetadas.'
  },
  {
    title: 'Enviando para IA',
    description: 'Enviando o contexto e o diff para o Gemini gerar o parecer de impacto técnico.'
  }
];

const EMPTY_LIST = [];
const AI_ANALYSIS_DISCLAIMER = 'A análise por IA é superficial, pode falhar ou omitir pontos relevantes. Consulte sempre o documento original antes de tomar qualquer decisão fiscal, técnica ou operacional.';
const DIFF_INITIAL_VISIBLE_LINES = 20;

const TONE_STYLES = {
  slate: {
    panel: 'border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/30',
    badge: 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700',
    label: 'text-gray-500 dark:text-gray-400',
    value: 'text-gray-900 dark:text-white',
    accent: 'text-gray-700 dark:text-gray-200'
  },
  sky: {
    panel: 'border-sky-200 dark:border-sky-900/40 bg-sky-50/80 dark:bg-sky-900/10',
    badge: 'bg-white dark:bg-gray-800 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-800',
    label: 'text-sky-700 dark:text-sky-300',
    value: 'text-sky-700 dark:text-sky-300',
    accent: 'text-sky-700 dark:text-sky-300'
  },
  emerald: {
    panel: 'border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/80 dark:bg-emerald-900/10',
    badge: 'bg-white dark:bg-gray-800 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
    label: 'text-emerald-700 dark:text-emerald-300',
    value: 'text-emerald-700 dark:text-emerald-300',
    accent: 'text-emerald-700 dark:text-emerald-300'
  },
  rose: {
    panel: 'border-rose-200 dark:border-rose-900/40 bg-rose-50/80 dark:bg-rose-900/10',
    badge: 'bg-white dark:bg-gray-800 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800',
    label: 'text-rose-700 dark:text-rose-300',
    value: 'text-rose-700 dark:text-rose-300',
    accent: 'text-rose-700 dark:text-rose-300'
  }
};

function SummaryMetric({ label, value, tone = 'slate', compact = false }) {
  const toneStyles = TONE_STYLES[tone] || TONE_STYLES.slate;

  return (
    <div className={`rounded-xl border px-3 py-2 ${toneStyles.panel}`}>
      <p className={`text-[9px] font-black uppercase tracking-widest ${toneStyles.label}`}>{label}</p>
      <p className={`mt-0.5 font-black ${compact ? 'text-lg' : 'text-xl'} ${toneStyles.value}`}>{value}</p>
    </div>
  );
}

function CountBadge({ children, tone = 'slate' }) {
  const toneStyles = TONE_STYLES[tone] || TONE_STYLES.slate;

  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black ${toneStyles.badge}`}>
      {children}
    </span>
  );
}

function formatCompactNumber(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return '';
  return number.toLocaleString('pt-BR');
}

function formatExtractionMethod(method = '') {
  if (method === 'pdf-parse') return 'PDF extraído';
  if (method === 'pdf-regex') return 'PDF fallback';
  if (method === 'html') return 'HTML extraído';
  if (method === 'text') return 'Texto extraído';
  if (method === 'fallback') return 'Texto fallback';
  if (method === 'fallback_metadata') return 'Metadados';
  return method ? 'Extraído' : '';
}

function parseVersionTime(item = {}) {
  const directDate = item.documentDate || item.document_date || item.createdAt || item.created_at;
  const directTime = directDate ? new Date(directDate).getTime() : 0;
  if (Number.isFinite(directTime) && directTime > 0) return directTime;

  const brDate = String(item.data || item.document_date_br || '').match(/(\d{2})\/(\d{2})\/(\d{4})/);
  if (brDate) {
    const [, day, month, year] = brDate;
    return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
  }

  const numericId = Number(item.id);
  return Number.isFinite(numericId) ? numericId : 0;
}

function sortVersionsByRecency(versions = []) {
  return [...versions].sort((left, right) => {
    const timeDelta = parseVersionTime(right) - parseVersionTime(left);
    if (timeDelta !== 0) return timeDelta;
    return Number(right?.id || 0) - Number(left?.id || 0);
  });
}

async function copyTextToClipboard(text) {
  const normalizedText = String(text || '');

  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(normalizedText);
      return;
    } catch {
      // Continua no fallback para contextos sem permissão explícita de clipboard.
    }
  }

  if (typeof document === 'undefined') return;

  const textArea = document.createElement('textarea');
  textArea.value = normalizedText;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('copy');
  document.body.removeChild(textArea);
}

function ExtractionBadges({ item }) {
  const badges = [
    formatExtractionMethod(item?.extractionMethod),
    item?.extractedPageCount ? `${formatCompactNumber(item.extractedPageCount)} pág.` : '',
    item?.extractedTextLength ? `${formatCompactNumber(item.extractedTextLength)} caracteres` : ''
  ].filter(Boolean);

  if (!badges.length) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {badges.map((badge) => (
        <span
          key={badge}
          className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[10px] font-bold text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
        >
          {badge}
        </span>
      ))}
    </div>
  );
}

function CompactCard({ children, tone = 'slate', className = '' }) {
  const toneStyles = TONE_STYLES[tone] || TONE_STYLES.slate;

  return (
    <div className={`rounded-2xl border p-3 shadow-sm ${toneStyles.panel} ${className}`}>
      {children}
    </div>
  );
}

function SectionHeader({ eyebrow, title, icon: Icon, children }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          {Icon ? <Icon className="h-3.5 w-3.5 text-sky-600 dark:text-sky-300" /> : null}
          <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">{eyebrow}</p>
        </div>
        {title ? (
          <h4 className="mt-1 break-words text-sm font-black text-gray-900 dark:text-white">{title}</h4>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function AiDisclaimer() {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-2 text-[11px] font-bold leading-5 text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-200">
      <div className="flex items-start gap-2">
        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <p>{AI_ANALYSIS_DISCLAIMER}</p>
      </div>
    </div>
  );
}

function ScrollableText({ children, className = '', maxHeightClass = 'max-h-40' }) {
  return (
    <div className={`${maxHeightClass} overflow-y-auto pr-1 ${className}`}>
      {children}
    </div>
  );
}

function ExpandableDiffLineText({ text, buttonClassName = 'text-sky-700 dark:text-sky-300' }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const lineRef = useRef(null);

  useLayoutEffect(() => {
    const line = lineRef.current;
    if (!line) return;

    const checkOverflow = () => {
      setIsOverflowing(line.scrollWidth > line.clientWidth + 1);
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [text, isExpanded]);

  return (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <span
        ref={lineRef}
        className={`min-w-0 flex-1 whitespace-pre ${isExpanded ? 'overflow-x-auto' : 'overflow-hidden text-ellipsis'}`}
      >
        {text}
      </span>
      {(isOverflowing || isExpanded) ? (
        <button
          type="button"
          onClick={() => setIsExpanded((previous) => !previous)}
          className={`shrink-0 font-sans text-[10px] font-black uppercase tracking-wider hover:underline ${buttonClassName}`}
        >
          {isExpanded ? 'Ver menos' : 'Ver mais'}
        </button>
      ) : null}
    </span>
  );
}

function MetaRow({ item, className = '' }) {
  return (
    <div className={`mt-2 flex flex-wrap gap-2 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 ${className}`}>
      <span>{item?.data || 'Sem data'}</span>
      <span>{item?.portalName || item?.sistema || 'Origem não identificada'}</span>
    </div>
  );
}

function DocumentMiniCard({ item, label, tone = 'slate', showDescription = false }) {
  const toneStyles = TONE_STYLES[tone] || TONE_STYLES.slate;

  return (
    <a
      href={buildDocumentSharePath(item)}
      className={`block min-w-0 rounded-xl border p-3 transition-colors hover:border-sky-300 dark:hover:border-sky-700 ${toneStyles.panel}`}
    >
      <p className={`text-[9px] font-black uppercase tracking-widest ${toneStyles.label}`}>{label}</p>
      <p className="mt-1 break-words text-sm font-bold leading-5 text-gray-900 dark:text-white">
        {item?.nome || 'Documento sem nome'}
      </p>
      <MetaRow item={item} />
      <ExtractionBadges item={item} />
      {showDescription && item?.description ? (
        <ExpandableDescription
          text={item.description}
          className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400"
          buttonClassName="text-sky-700 dark:text-sky-300"
        />
      ) : null}
    </a>
  );
}

function DetailList({ title, items = [], emptyMessage, tone = 'slate', scrollable = false, maxHeightClass = 'max-h-56' }) {
  const toneStyles = TONE_STYLES[tone] || TONE_STYLES.slate;

  return (
    <div className={`rounded-2xl border p-3 ${toneStyles.panel}`}>
      <div className="flex items-center justify-between gap-3">
        <p className={`text-[9px] font-black uppercase tracking-widest ${toneStyles.label}`}>{title}</p>
        <CountBadge tone={tone}>{items.length}</CountBadge>
      </div>

      <div className={`mt-2 space-y-2 ${scrollable ? `${maxHeightClass} overflow-y-auto pr-1` : ''}`}>
        {items.length ? (
          items.map((item, index) => (
            <div
              key={`${title}-${index}`}
              className="whitespace-pre-wrap break-words rounded-lg border border-white/80 bg-white/90 px-2.5 py-2 text-xs leading-5 text-gray-700 shadow-sm dark:border-gray-800/60 dark:bg-gray-950/30 dark:text-gray-200"
            >
              {item}
            </div>
          ))
        ) : (
          <div className="rounded-lg border border-dashed border-white/70 bg-white/60 px-2.5 py-3 text-xs leading-5 text-gray-500 dark:border-gray-800/60 dark:bg-gray-950/20 dark:text-gray-400">
            {emptyMessage}
          </div>
        )}
      </div>
    </div>
  );
}

function formatImpactLabel(value = '') {
  if (value === 'alto') return 'Alto impacto';
  if (value === 'baixo') return 'Baixo impacto';
  return 'Impacto médio';
}

function formatConfidenceLabel(value = '') {
  if (value === 'alta') return 'Alta confiança';
  if (value === 'baixa') return 'Baixa confiança';
  return 'Confiança média';
}

function AiAnalysisCard({ ai }) {
  if (!ai?.summary) return null;

  return (
    <CompactCard tone="sky">
      <SectionHeader eyebrow="Leitura com IA" title="Resumo semântico">
        <div className="flex flex-wrap gap-1.5">
          <CountBadge tone="sky">{formatImpactLabel(ai.impactLevel)}</CountBadge>
          <CountBadge tone="slate">{formatConfidenceLabel(ai.confidence)}</CountBadge>
          <CountBadge tone="emerald">{ai.model || 'Gemini'}</CountBadge>
        </div>
      </SectionHeader>

      <ScrollableText className="mt-2" maxHeightClass="max-h-44">
        <p className="text-sm leading-6 text-gray-700 dark:text-gray-200">
          {ai.summary}
        </p>
      </ScrollableText>

      <div className="mt-3 grid gap-3 xl:grid-cols-2 2xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]">
        <div className="rounded-2xl border border-sky-200 bg-white/80 p-3 dark:border-sky-800 dark:bg-gray-900/40">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[9px] font-black uppercase tracking-widest text-sky-700 dark:text-sky-300">
              Temas alterados
            </p>
            <CountBadge tone="sky">{ai.changedTopics?.length || 0}</CountBadge>
          </div>
          <div className="mt-2 max-h-32 overflow-y-auto pr-1">
            <div className="flex flex-wrap gap-1.5">
              {ai.changedTopics?.length ? (
                ai.changedTopics.map((topic) => (
                  <span
                    key={topic}
                    className="rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-bold text-sky-700 dark:border-sky-800 dark:bg-sky-900/20 dark:text-sky-300"
                  >
                    {topic}
                  </span>
                ))
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">Sem temas destacados.</p>
              )}
            </div>
          </div>
        </div>

        <DetailList
          title="Mudanças relevantes"
          items={ai.relevantChanges || []}
          emptyMessage="A IA não destacou mudanças concretas adicionais."
          tone="emerald"
          scrollable
        />

        <DetailList
          title="Impactos práticos"
          items={ai.practicalImpacts || []}
          emptyMessage="A IA não destacou impactos práticos adicionais."
          tone="slate"
          scrollable
        />

        <DetailList
          title="Checks recomendados"
          items={ai.recommendedChecks || []}
          emptyMessage="A IA não sugeriu verificações adicionais."
          tone="emerald"
          scrollable
        />
      </div>
    </CompactCard>
  );
}

function BaselineAnalysisCard({ baselineDocument, ai }) {
  if (!baselineDocument) return null;

  return (
    <CompactCard tone="emerald">
      <SectionHeader eyebrow="NT inicial" title="Leitura base da série">
        {ai?.confidence ? <CountBadge tone="emerald">{formatConfidenceLabel(ai.confidence)}</CountBadge> : null}
      </SectionHeader>

      <a
        href={buildDocumentSharePath(baselineDocument)}
        className="mt-2 block break-words text-sm font-bold leading-5 text-gray-900 hover:text-emerald-700 dark:text-white dark:hover:text-emerald-300"
      >
        {baselineDocument.nome}
      </a>
      <MetaRow item={baselineDocument} />

      {ai?.summary ? (
        <>
          <ScrollableText className="mt-3" maxHeightClass="max-h-44">
            <p className="text-sm leading-6 text-gray-700 dark:text-gray-200">
              {ai.summary}
            </p>
          </ScrollableText>
          <div className="mt-3 grid gap-3 xl:grid-cols-3">
            <DetailList
              title="Escopo da NT"
              items={ai.documentScope ? [ai.documentScope] : []}
              emptyMessage="Sem escopo resumido disponível."
              tone="emerald"
              scrollable
            />
            <DetailList
              title="Impactos de implementação"
              items={ai.implementationImpacts || []}
              emptyMessage="A IA não destacou impactos adicionais."
              tone="slate"
              scrollable
            />
            <DetailList
              title="Foco de validação"
              items={ai.validationFocus || []}
              emptyMessage="A IA não sugeriu focos de validação."
              tone="sky"
              scrollable
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {(ai.keyTopics || []).length ? ai.keyTopics.map((topic) => (
              <span
                key={topic}
                className="rounded-full border border-emerald-200 bg-white px-2 py-0.5 text-[10px] font-bold text-emerald-700 dark:border-emerald-800 dark:bg-gray-800 dark:text-emerald-300"
              >
                {topic}
              </span>
            )) : (
              <p className="text-xs text-gray-500 dark:text-gray-400">Sem temas-chave destacados pela IA.</p>
            )}
          </div>
        </>
      ) : (
        <div className="mt-3 rounded-xl border border-dashed border-emerald-200 bg-white/70 p-3 text-xs leading-5 text-gray-500 dark:border-emerald-800 dark:bg-gray-900/20 dark:text-gray-400">
          Ainda não existe análise em cache da NT inicial para esta série.
        </div>
      )}
    </CompactCard>
  );
}

function HistoricalPairCard({ pair }) {
  const changedTerms = pair.diff?.changedTerms || [];
  const addedItems = pair.diff?.added || [];
  const removedItems = pair.diff?.removed || [];

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="grid gap-2 xl:grid-cols-2">
        <DocumentMiniCard item={pair.newer} label="Mais nova" tone="sky" />
        <DocumentMiniCard item={pair.older} label="Comparada com" tone="slate" />
      </div>

      <div className="mt-3 grid gap-3 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
        <div className="grid gap-3 xl:grid-cols-2">
          <DetailList
            title="Trechos adicionados"
            items={addedItems}
            emptyMessage="Sem trechos adicionados detectados."
            tone="emerald"
          />
          <DetailList
            title="Trechos removidos"
            items={removedItems}
            emptyMessage="Sem trechos removidos detectados."
            tone="rose"
          />
        </div>

        <div className="rounded-2xl border border-sky-200 bg-sky-50/80 p-3 dark:border-sky-900/40 dark:bg-sky-900/10">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[9px] font-black uppercase tracking-widest text-sky-700 dark:text-sky-300">Termos</p>
            <CountBadge tone="sky">{changedTerms.length}</CountBadge>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {changedTerms.length ? (
              changedTerms.map((token) => (
                <span
                  key={`${pair.id}-${token}`}
                  className="rounded-full border border-sky-200 bg-white px-2 py-0.5 text-[10px] font-bold text-sky-700 dark:border-sky-800 dark:bg-gray-800 dark:text-sky-300"
                >
                  {token}
                </span>
              ))
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400">Sem termos novos relevantes.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TimelineVersionNode({ item, label, isActive = false, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-44 shrink-0 flex-col items-center text-center"
    >
      <span
        className={`relative z-10 flex h-7 w-7 items-center justify-center rounded-full border-4 transition-all ${
          isActive
            ? 'border-sky-200 bg-sky-600 text-white shadow-lg shadow-sky-500/25 dark:border-sky-900'
            : 'border-gray-200 bg-white text-gray-500 group-hover:border-sky-200 group-hover:bg-sky-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:group-hover:border-sky-800 dark:group-hover:bg-sky-900/20'
        }`}
      >
        <span className="h-2.5 w-2.5 rounded-full bg-current" />
      </span>
      <span className={`mt-2 text-[9px] font-black uppercase tracking-widest ${isActive ? 'text-sky-700 dark:text-sky-300' : 'text-gray-400 dark:text-gray-500'}`}>
        {label}
      </span>
      <span className="mt-1 block max-h-8 overflow-y-auto pr-1 text-xs font-bold leading-4 text-gray-800 group-hover:text-sky-700 dark:text-gray-100 dark:group-hover:text-sky-300">
        {item?.nome || 'Versão sem nome'}
      </span>
      <span className="mt-1 text-[10px] font-bold text-gray-400 dark:text-gray-500">
        {item?.data || 'Sem data'}
      </span>
    </button>
  );
}


function TimelineComparisonSection({ versions = [], pairs = [] }) {
  const timelineVersions = versions.length
    ? versions
    : pairs.reduce((items, pair, index) => {
      if (index === 0 && pair.newer) items.push(pair.newer);
      if (pair.older) items.push(pair.older);
      return items;
    }, []);
  const [selectedPairId, setSelectedPairId] = useState('');

  useEffect(() => {
    if (!pairs.length) {
      setSelectedPairId('');
      return;
    }

    setSelectedPairId((current) => (
      pairs.some((pair) => pair.id === current) ? current : pairs[0].id
    ));
  }, [pairs]);

  const selectedPair = pairs.find((pair) => pair.id === selectedPairId) || pairs[0] || null;
  const activeVersionIds = selectedPair
    ? new Set([String(selectedPair.newer?.id), String(selectedPair.older?.id)])
    : new Set();

  const selectPairNearVersion = (index) => {
    if (!pairs.length) return;
    const nextPair = pairs[Math.min(index, pairs.length - 1)] || pairs[pairs.length - 1];
    setSelectedPairId(nextPair.id);
  };

  return (
    <CompactCard tone="slate" className="bg-white dark:bg-gray-800">
      <SectionHeader eyebrow="Linha do tempo da faixa" title="Passos entre as versões selecionadas">
        <CountBadge tone="slate">{pairs.length} comparações</CountBadge>
      </SectionHeader>

      {pairs.length === 0 ? (
        <div className="mt-3 rounded-xl border border-dashed border-gray-200 bg-gray-50/80 p-3 text-xs leading-5 text-gray-500 dark:border-gray-700 dark:bg-gray-900/30 dark:text-gray-400">
          Não há transições adicionais dentro da faixa selecionada.
        </div>
      ) : (
        <>
          <div className="mt-4 overflow-x-auto pb-2">
            <div className="min-w-max px-4">
              <div className="relative flex items-start">
                <div className="absolute left-20 right-20 top-[0.85rem] h-1 rounded-full bg-gray-200 dark:bg-gray-700" />

                {timelineVersions.map((item, index) => {
                  const isLast = index === timelineVersions.length - 1;
                  const isActive = activeVersionIds.has(String(item?.id));
                  const milestoneLabel = index === 0
                    ? 'De'
                    : isLast
                      ? 'Até'
                      : `Marco ${index + 1}`;

                  return (
                    // Exibe apenas o nó de versão — sem botão intermediário entre os marcos
                    <TimelineVersionNode
                      key={`${item?.id || item?.url || index}-timeline`}
                      item={item}
                      label={milestoneLabel}
                      isActive={isActive}
                      onClick={() => selectPairNearVersion(index)}
                    />
                  );
                })}
              </div>
            </div>
          </div>

          {selectedPair ? (
            <div className="mt-4">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">
                  Comparação selecionada
                </p>
                <CountBadge tone="sky">
                  {selectedPair.diff?.changedTerms?.length || 0} termos
                </CountBadge>
              </div>
              <HistoricalPairCard pair={selectedPair} />
            </div>
          ) : null}
        </>
      )}
    </CompactCard>
  );
}

function formatVersionOptionLabel(item) {
  const parts = [item?.data || 'Sem data', item?.nome || 'Sem nome'];
  return parts.filter(Boolean).join(' • ');
}

function ComparisonRangeSelector({ comparison, onCompareRange, disabled = false }) {
  const availableVersions = useMemo(
    () => comparison?.availableVersions || [],
    [comparison?.availableVersions]
  );
  const selectedRange = comparison?.selectedRange || {};
  const defaultNewerId = comparison?.current?.id ? String(comparison.current.id) : '';
  const defaultOlderId = comparison?.previous?.id ? String(comparison.previous.id) : '';

  // Inicializa com selectedRange (se existir) ou com os valores padrão de current/previous
  const [newerId, setNewerId] = useState(
    () => (selectedRange.newerId ? String(selectedRange.newerId) : defaultNewerId)
  );
  const [olderId, setOlderId] = useState(
    () => (selectedRange.olderId ? String(selectedRange.olderId) : defaultOlderId)
  );

  // Sincroniza APENAS quando selectedRange mudar explicitamente via backend,
  // ignorando mudanças nos defaults para não sobrescrever a seleção do usuário
  const prevSelectedRangeRef = useRef(selectedRange);
  useEffect(() => {
    const prev = prevSelectedRangeRef.current;
    const rangeNewerChanged = selectedRange.newerId !== prev.newerId;
    const rangeOlderChanged = selectedRange.olderId !== prev.olderId;
    prevSelectedRangeRef.current = selectedRange;

    if (rangeNewerChanged) {
      setNewerId(selectedRange.newerId ? String(selectedRange.newerId) : defaultNewerId);
    }
    if (rangeOlderChanged) {
      setOlderId(selectedRange.olderId ? String(selectedRange.olderId) : defaultOlderId);
    }
  }, [selectedRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const versionOptions = useMemo(
    () => availableVersions.map((item) => ({
      value: String(item.id),
      label: formatVersionOptionLabel(item)
    })),
    [availableVersions]
  );

  const newerIndex = useMemo(
    () => availableVersions.findIndex((item) => String(item.id) === String(newerId)),
    [availableVersions, newerId]
  );

  const olderOptions = useMemo(() => {
    if (newerIndex < 0) return versionOptions;
    return availableVersions
      .slice(newerIndex + 1)
      .map((item) => ({
        value: String(item.id),
        label: formatVersionOptionLabel(item)
      }));
  }, [availableVersions, newerIndex, versionOptions]);

  useEffect(() => {
    if (olderOptions.length === 0) {
      setOlderId('');
      return;
    }

    if (!olderOptions.some((item) => item.value === olderId)) {
      setOlderId(olderOptions[0].value);
    }
  }, [olderId, olderOptions]);

  const selectedOlderIndex = useMemo(
    () => availableVersions.findIndex((item) => String(item.id) === String(olderId)),
    [availableVersions, olderId]
  );

  const includedCount = newerIndex >= 0 && selectedOlderIndex > newerIndex
    ? selectedOlderIndex - newerIndex + 1
    : 0;

  const intermediateCount = includedCount > 1 ? Math.max(includedCount - 2, 0) : 0;
  const canCompare = Boolean(newerId && olderId && includedCount >= 2);

  return (
    <CompactCard tone="slate" className="bg-white dark:bg-gray-800">
      <SectionHeader eyebrow="Seleção" title="Versões da análise">
        <div className="flex flex-wrap gap-1.5">
          <CountBadge tone="sky">{includedCount || 0} na faixa</CountBadge>
          <CountBadge tone="slate">{intermediateCount} intermediárias</CountBadge>
        </div>
      </SectionHeader>

      <div className="mt-3 grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
        <div className="min-w-0">
          <p className="mb-1.5 text-[9px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">Mais nova</p>
          <SingleSelectDropdown
            options={versionOptions}
            selectedOption={newerId}
            onSelectionChange={setNewerId}
            title="Selecionar versão mais nova"
            disabled={disabled || versionOptions.length === 0}
            Icon={GitCompareArrows}
            heightClass="h-10"
          />
        </div>

        <div className="min-w-0">
          <p className="mb-1.5 text-[9px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">Mais antiga</p>
          <SingleSelectDropdown
            options={olderOptions}
            selectedOption={olderId}
            onSelectionChange={setOlderId}
            title="Selecionar versão mais antiga"
            disabled={disabled || olderOptions.length === 0}
            Icon={History}
            heightClass="h-10"
          />
        </div>

        <button
          type="button"
          onClick={() => onCompareRange?.({
            newerDocumentId: newerId,
            olderDocumentId: olderId
          })}
          disabled={disabled || !canCompare}
          aria-label="Comparar versões selecionadas"
          title="Comparar versões selecionadas"
          className="inline-flex h-10 w-full items-center justify-center gap-2 self-end rounded-xl bg-sky-600 px-4 text-white transition-colors hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50 lg:w-10 lg:px-0"
        >
          <GitCompareArrows className="h-4 w-4" />
          <span className="text-[11px] font-black uppercase tracking-widest lg:hidden">Comparar versões</span>
          <span className="sr-only">Comparar</span>
        </button>
      </div>

      <p className="mt-2 text-[11px] font-bold text-gray-500 dark:text-gray-400">
        {availableVersions.length} versões disponíveis nessa série.
      </p>
    </CompactCard>
  );
}

// Componente premium que exibe as linhas de diff em formato Git Patch intercalado verticalmente e com alto contraste
function GitDiffViewer({ diffPreview, searchTerm = '' }) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Filtra as linhas do diff se houver um termo de busca ativa
  const lines = useMemo(() => {
    if (!diffPreview) return [];
    const rawLines = diffPreview.split('\n');
    if (!searchTerm) return rawLines;
    
    return rawLines.filter((line) => 
      line.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [diffPreview, searchTerm]);

  useEffect(() => {
    setIsExpanded(false);
  }, [diffPreview, searchTerm]);

  const shouldCollapseLines = lines.length > DIFF_INITIAL_VISIBLE_LINES;
  const visibleLines = shouldCollapseLines && !isExpanded
    ? lines.slice(0, DIFF_INITIAL_VISIBLE_LINES)
    : lines;
  const hiddenLinesCount = Math.max(lines.length - DIFF_INITIAL_VISIBLE_LINES, 0);

  if (!diffPreview) {
    return (
      <div className="flex min-h-[10rem] flex-col items-center justify-center gap-2 text-gray-400 dark:text-gray-500 p-6 text-center">
        <GitCompare className="h-8 w-8 opacity-40 animate-pulse text-sky-600 dark:text-sky-400" />
        <p className="text-xs font-bold uppercase tracking-wider">
          Nenhuma alteração textual direta detectada no corpo do layout.
        </p>
        <p className="text-[10px] text-gray-400 dark:text-gray-500 max-w-xs">
          O portal fiscal do SEFAZ pode ter publicado apenas metadados ou ajustes estruturais sem mudança de leiaute.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-700/60 dark:bg-gray-900/40">
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/50 px-4 py-2.5 dark:border-gray-800 dark:bg-gray-900/50">
        <span className="text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
          Visualizador de Diferenças Intercaladas (Git Diff Style)
        </span>
        <span className="rounded-full bg-slate-100 dark:bg-gray-800 px-2 py-0.5 text-[9px] font-black text-slate-500 dark:text-gray-400">
          {shouldCollapseLines && !isExpanded ? `${visibleLines.length} de ${lines.length}` : lines.length} linhas {searchTerm ? 'filtradas' : 'comparáveis'}
        </span>
      </div>
      <div className="max-h-96 overflow-y-auto p-4 font-mono text-[11px] leading-relaxed space-y-1.5 bg-gray-50/30 dark:bg-gray-950/20">
        {lines.length > 0 ? (
          <>
            {visibleLines.map((line, idx) => {
              const isAddedHeader = line.startsWith('+++');
              const isRemovedHeader = line.startsWith('---');
              const isAdded = line.startsWith('+') && !isAddedHeader;
              const isRemoved = line.startsWith('-') && !isRemovedHeader;

              if (isAddedHeader || isRemovedHeader) {
                return (
                  <div
                    key={idx}
                    className={`flex min-w-0 items-center gap-1 rounded-lg border-l-4 px-3 py-1.5 shadow-sm ${
                      isAddedHeader
                        ? 'border-emerald-500 bg-emerald-50/70 text-emerald-800 dark:bg-emerald-950/20 dark:text-emerald-300'
                        : 'border-rose-500 bg-rose-50/70 text-rose-800 dark:bg-rose-950/20 dark:text-rose-300'
                    }`}
                  >
                    <ExpandableDiffLineText
                      text={line}
                      buttonClassName={isAddedHeader ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}
                    />
                  </div>
                );
              }

              if (isAdded) {
                return (
                  <div
                    key={idx}
                    className="flex min-w-0 items-center gap-1 rounded-lg bg-emerald-50/70 text-emerald-800 dark:bg-emerald-950/20 dark:text-emerald-300 border-l-4 border-emerald-500 px-3 py-1.5 shadow-sm"
                  >
                    <span className="select-none font-black text-emerald-500/70 mr-2 shrink-0">+</span>
                    <ExpandableDiffLineText text={line.substring(1)} buttonClassName="text-emerald-700 dark:text-emerald-300" />
                  </div>
                );
              }

              if (isRemoved) {
                return (
                  <div
                    key={idx}
                    className="flex min-w-0 items-center gap-1 rounded-lg bg-rose-50/70 text-rose-800 dark:bg-rose-950/20 dark:text-rose-300 border-l-4 border-rose-500 px-3 py-1.5 shadow-sm"
                  >
                    <span className="select-none font-black text-rose-500/70 mr-2 shrink-0">-</span>
                    <ExpandableDiffLineText text={line.substring(1)} buttonClassName="text-rose-700 dark:text-rose-300" />
                  </div>
                );
              }

              // Renderização de linhas normais de contexto
              return (
                <div
                  key={idx}
                  className="flex min-w-0 items-center gap-1 px-4 py-0.5 text-gray-500 dark:text-gray-400"
                >
                  <span className="select-none text-gray-300 dark:text-gray-700 mr-4 shrink-0">•</span>
                  <ExpandableDiffLineText text={line} />
                </div>
              );
            })}

            {shouldCollapseLines ? (
              <div className="flex justify-center border-t border-gray-200/70 pt-3 dark:border-gray-800">
                <button
                  type="button"
                  aria-expanded={isExpanded}
                  onClick={() => setIsExpanded((previous) => !previous)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-white px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-sky-700 shadow-sm transition-colors hover:bg-sky-50 dark:border-sky-900/50 dark:bg-gray-900 dark:text-sky-300 dark:hover:bg-sky-950/20"
                >
                  {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  {isExpanded ? 'Ver menos' : `Ver mais ${hiddenLinesCount} linhas`}
                </button>
              </div>
            ) : null}
          </>
        ) : (
          <div className="py-6 text-center text-xs font-bold text-gray-500 dark:text-gray-500 uppercase tracking-widest">
            Nenhuma linha contendo o termo "{searchTerm}" encontrada no diff.
          </div>
        )}
      </div>
    </div>
  );
}

// Componente de cartão premium para cláusulas incluídas/excluídas com visual moderno HSL e funcionalidade de cópia rápida
function ClauseCard({ text, type = 'added' }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await copyTextToClipboard(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const isAdded = type === 'added';
  const Icon = isAdded ? Plus : Minus;

  return (
    <div className={`group relative rounded-xl border border-l-4 p-3.5 transition-all hover:shadow-md ${
      isAdded
        ? 'border-emerald-100 border-l-emerald-500 bg-emerald-50/40 dark:border-emerald-900/30 dark:border-l-emerald-500 dark:bg-emerald-950/10 hover:border-emerald-200 hover:bg-emerald-50/70'
        : 'border-rose-100 border-l-rose-500 bg-rose-50/40 dark:border-rose-900/30 dark:border-l-rose-500 dark:bg-rose-950/10 hover:border-rose-200 hover:bg-rose-50/70'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-2.5">
          <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-black select-none ${
            isAdded
              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
              : 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300'
          }`}>
            <Icon className="h-3.5 w-3.5" />
          </span>
          <ExpandableDescription
            text={String(text || '')}
            className="min-w-0 text-xs text-gray-700 dark:text-gray-200 font-medium break-words whitespace-pre-wrap"
            buttonClassName={isAdded ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}
          />
        </div>

        <button
          type="button"
          onClick={handleCopy}
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[10px] font-black uppercase tracking-widest transition-all ${
            copied
              ? 'bg-emerald-600 text-white shadow-sm scale-95'
              : 'bg-white dark:bg-gray-800 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:text-gray-300'
          }`}
          title="Copiar texto desta cláusula para a área de transferência"
        >
          {copied ? (
            <>
              <Check className="h-3 w-3" />
              Copiado!
            </>
          ) : (
            <>
              <Copy className="h-3 w-3" />
              Copiar cláusula
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// Componente premium que exibe a análise de contexto e escopo inteligente do documento atual gerada e salva via IA
function DocumentContextCard({ currentDocument, ai }) {
  if (!currentDocument && !ai?.summary) return null;

  const keyTopics = Array.isArray(ai?.keyTopics) ? ai.keyTopics : [];
  const implementationImpacts = Array.isArray(ai?.implementationImpacts) ? ai.implementationImpacts : [];
  const validationFocus = Array.isArray(ai?.validationFocus) ? ai.validationFocus : [];
  const hasAiContext = Boolean(ai?.summary);

  return (
    <CompactCard tone="sky" className="bg-white dark:bg-gray-800 border-sky-100 dark:border-sky-900/30">
      <SectionHeader
        eyebrow="IA • Contexto da Nota Técnica"
        title="Contexto da NT de Referência"
        icon={Sparkles}
      >
        <div className="flex flex-wrap gap-1.5">
          <CountBadge tone="sky">{hasAiContext ? 'Contexto em cache' : 'Contexto pendente'}</CountBadge>
          {ai?.confidence ? <CountBadge tone="slate">{formatConfidenceLabel(ai.confidence)}</CountBadge> : null}
        </div>
      </SectionHeader>

      <div className="mt-4 space-y-4">
        {currentDocument ? (
          <div className="rounded-xl border border-sky-100 bg-sky-50/40 p-3 dark:border-sky-900/40 dark:bg-sky-950/10">
            <p className="text-[9px] font-black uppercase tracking-widest text-sky-700 dark:text-sky-300">
              Documento analisado
            </p>
            <p className="mt-1 break-words text-sm font-bold leading-5 text-gray-900 dark:text-white">
              {currentDocument.nome || 'Documento de referência'}
            </p>
            <MetaRow item={currentDocument} />
          </div>
        ) : null}

        <div className="grid gap-3.5 md:grid-cols-2">
          <div className="p-3.5 bg-sky-50/30 rounded-xl border border-sky-100/50 dark:bg-sky-950/10 dark:border-sky-900/20">
            <span className="text-[10px] font-black uppercase tracking-widest text-sky-700 dark:text-sky-300 block mb-1">
              Resumo da Nota Técnica
            </span>
            <ScrollableText maxHeightClass="max-h-40">
              <p className="text-xs leading-5 text-gray-700 dark:text-gray-200 font-semibold break-words">
                {ai?.summary || 'A análise contextual por IA ainda não foi gerada para esta versão.'}
              </p>
            </ScrollableText>
          </div>

          <div className="p-3.5 bg-gray-50/50 rounded-xl border border-gray-100/80 dark:bg-gray-900/10 dark:border-gray-800/50">
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 block mb-1">
              Objetivo Central e Escopo Funcional
            </span>
            <ScrollableText maxHeightClass="max-h-40">
              <p className="text-xs leading-5 text-gray-600 dark:text-gray-300 break-words">
                {ai?.documentScope || 'Escopo funcional indisponível no cache atual.'}
              </p>
            </ScrollableText>
          </div>
        </div>

        <div className="grid gap-3.5 md:grid-cols-3 text-xs">
          <div className="p-3.5 bg-white dark:bg-gray-900/40 rounded-xl border border-gray-200 dark:border-gray-700/60 shadow-sm">
            <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 block mb-2 pb-1 border-b border-gray-100 dark:border-gray-800">
              Temas-Chave Principais
            </span>
            <ul className="max-h-44 space-y-1.5 overflow-y-auto pr-1 list-disc list-inside text-gray-600 dark:text-gray-300">
              {keyTopics.length ? keyTopics.map((topic, idx) => (
                <li key={idx} className="break-words leading-relaxed">{topic}</li>
              )) : <li className="text-gray-400">Nenhum tema-chave detectado.</li>}
            </ul>
          </div>

          <div className="p-3.5 bg-white dark:bg-gray-900/40 rounded-xl border border-gray-200 dark:border-gray-700/60 shadow-sm">
            <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 block mb-2 pb-1 border-b border-gray-100 dark:border-gray-800">
              Impactos na Implementação
            </span>
            <ul className="max-h-44 space-y-1.5 overflow-y-auto pr-1 list-disc list-inside text-gray-600 dark:text-gray-300">
              {implementationImpacts.length ? implementationImpacts.map((impact, idx) => (
                <li key={idx} className="break-words leading-relaxed">{impact}</li>
              )) : <li className="text-gray-400">Nenhum impacto de implementação mapeado.</li>}
            </ul>
          </div>

          <div className="p-3.5 bg-white dark:bg-gray-900/40 rounded-xl border border-gray-200 dark:border-gray-700/60 shadow-sm">
            <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 block mb-2 pb-1 border-b border-gray-100 dark:border-gray-800">
              Foco de Validação do Fisco
            </span>
            <ul className="max-h-44 space-y-1.5 overflow-y-auto pr-1 list-disc list-inside text-gray-600 dark:text-gray-300">
              {validationFocus.length ? validationFocus.map((focus, idx) => (
                <li key={idx} className="break-words leading-relaxed">{focus}</li>
              )) : <li className="text-gray-400">Nenhum foco de validação específico.</li>}
            </ul>
          </div>
        </div>
      </div>
    </CompactCard>
  );
}

// Tela de Configuração Inicial da Comparação de Notas Técnicas.
// Garante o controle total do usuário ao comparar versões e detalha os passos do pipeline de forma elegante.
function ComparisonSetupScreen({
  sourceDocument,
  olderVersions,
  selectedOlderId,
  onSelectOlderId,
  onStartComparison,
  isFetching
}) {
  const selectedOlderDoc = useMemo(() => {
    return olderVersions.find((v) => String(v.id) === String(selectedOlderId)) || null;
  }, [olderVersions, selectedOlderId]);

  const canStart = Boolean(selectedOlderId && sourceDocument?.id);

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="border-b border-gray-100 bg-gray-50/50 px-4 py-4 dark:border-gray-700/50 dark:bg-gray-900/10">
          <h4 className="text-sm font-black uppercase tracking-wider text-gray-700 dark:text-gray-200 flex items-center gap-2">
            <GitCompareArrows className="h-4 w-4 text-sky-600 dark:text-sky-400" />
            Configuração da Comparação de Versões
          </h4>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Selecione uma versão anterior do histórico desta série fiscal para analisar mudanças normativas, campos de validação e termos alterados.
          </p>
        </div>

        <div className="p-4 grid gap-4 lg:grid-cols-2">
          {/* Lado Esquerdo: Referência Atual */}
          <div className="space-y-3 min-w-0">
            <p className="text-[10px] font-black uppercase tracking-widest text-sky-600 dark:text-sky-400">
              Documento Novo (Referência)
            </p>
            <div className="relative overflow-hidden rounded-xl border border-sky-100 bg-sky-50/20 p-4 dark:border-sky-950/40 dark:bg-sky-950/10 hover:shadow-md transition-shadow">
              <span className="absolute right-3 top-3 inline-flex items-center rounded-full bg-sky-100 dark:bg-sky-950 px-2.5 py-0.5 text-[9px] font-black text-sky-700 dark:text-sky-300">
                REFERÊNCIA
              </span>
              <p className="text-xs font-black uppercase tracking-wider text-gray-400 dark:text-gray-500">
                {sourceDocument?.sistema || 'Série Fiscal'} • {sourceDocument?.tipo || 'Nota Técnica'}
              </p>
              <h5 className="mt-1.5 text-base font-bold text-gray-900 dark:text-white leading-6 break-words">
                {sourceDocument?.nome}
              </h5>
              <div className="mt-2.5 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span>Publicado em: <strong>{sourceDocument?.data || 'Sem data'}</strong></span>
                <span>•</span>
                <span>Origem: <strong>{sourceDocument?.portalName || sourceDocument?.portalSlug || 'SEFAZ'}</strong></span>
              </div>
              {sourceDocument?.description && (
                <ExpandableDescription
                  text={sourceDocument.description}
                  className="mt-3 rounded-lg border border-gray-100 bg-gray-50 p-2.5 text-xs text-gray-500 dark:border-gray-800 dark:bg-gray-900/50 dark:text-gray-400"
                  buttonClassName="text-sky-700 dark:text-sky-300"
                />
              )}
            </div>
          </div>

          {/* Lado Direito: Seleção do Histórico */}
          <div className="space-y-3 min-w-0">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-600 dark:text-slate-400">
              Documento Antigo (Histórico)
            </p>

            <div className="space-y-3">
              <div>
                <SingleSelectDropdown
                  options={olderVersions.map((v) => ({
                    value: String(v.id),
                    label: formatVersionOptionLabel(v)
                  }))}
                  selectedOption={selectedOlderId}
                  onSelectionChange={onSelectOlderId}
                  title="Selecionar versão anterior..."
                  disabled={isFetching || olderVersions.length === 0}
                  Icon={History}
                  heightClass="h-11"
                />
              </div>

              {selectedOlderDoc ? (
                <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 dark:border-gray-700 dark:bg-gray-900/20">
                  <p className="text-xs font-black uppercase tracking-wider text-gray-400 dark:text-gray-500">
                    Detalhes do arquivo selecionado
                  </p>
                  <h5 className="mt-1.5 text-sm font-bold text-gray-900 dark:text-gray-100 break-words">
                    {selectedOlderDoc.nome}
                  </h5>
                  <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <span>Data: <strong>{selectedOlderDoc.data || 'Sem data'}</strong></span>
                    {selectedOlderDoc.sourceByteLength && (
                      <>
                        <span>•</span>
                        <span>Tamanho: <strong>{formatBytes(selectedOlderDoc.sourceByteLength)}</strong></span>
                      </>
                    )}
                  </div>
                  {selectedOlderDoc.description && (
                    <ExpandableDescription
                      text={selectedOlderDoc.description}
                      className="mt-2.5 text-xs text-gray-500 dark:text-gray-400"
                      buttonClassName="text-sky-700 dark:text-sky-300"
                    />
                  )}
                </div>
              ) : (
                <div className="flex h-32 items-center justify-center rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                  <p className="text-xs font-bold text-gray-400 dark:text-gray-500">
                    Nenhuma versão anterior selecionada
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Barra de Ação do Rodapé com Botão de Comparação */}
        <div className="border-t border-gray-200 bg-gray-50/80 px-4 py-4 dark:border-gray-700/50 dark:bg-gray-900/20 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-xs text-gray-500 dark:text-gray-400 text-center sm:text-left">
            {olderVersions.length > 0 ? (
              <span><strong>{olderVersions.length}</strong> versões disponíveis para comparação nesta série fiscal.</span>
            ) : (
              <span className="text-amber-600 dark:text-amber-400 font-bold">Nenhuma outra versão anterior disponível para comparação direta.</span>
            )}
          </div>
          <button
            type="button"
            disabled={!canStart || isFetching}
            onClick={() => onStartComparison?.({
              newerDocumentId: String(sourceDocument.id),
              olderDocumentId: selectedOlderId
            })}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-sky-600 hover:bg-sky-700 active:bg-sky-800 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed px-6 py-3 text-xs font-black uppercase tracking-widest text-white shadow-md transition-all active:scale-95"
          >
            <GitCompare className="h-4 w-4 animate-pulse" />
            Iniciar Comparação
          </button>
        </div>
      </div>

      {/* Visão de Processos do Pipeline */}
      <div className="rounded-2xl border border-gray-100 bg-gray-50/30 p-4 dark:border-gray-700 dark:bg-gray-900/10">
        <h5 className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">
          Como funciona a comparação
        </h5>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <span className="text-xs font-black text-sky-600 dark:text-sky-400 block mb-1">01 • Extração de Texto</span>
            <p className="text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
              O PDF original é baixado e seu texto integral é extraído limpando ruídos e sumários.
            </p>
          </div>
          <div className="p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <span className="text-xs font-black text-sky-600 dark:text-sky-400 block mb-1">02 • Análise de Diferenças Textuais</span>
            <p className="text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
              Calculamos as linhas exatas adicionadas e excluídas de forma intercalada.
            </p>
          </div>
          <div className="p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <span className="text-xs font-black text-sky-600 dark:text-sky-400 block mb-1">03 • Contexto da NT</span>
            <p className="text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
              A IA analisa e salva o objetivo central e escopo normativo do documento de referência.
            </p>
          </div>
          <div className="p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <span className="text-xs font-black text-sky-600 dark:text-sky-400 block mb-1">04 • Relatório Faltas/IA</span>
            <p className="text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
              O Gemini processa as modificações estruturais e gera um parecer de impactos técnicos.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Exibe os cards do comparador fiscal processados
function ComparisonCard({ comparison }) {
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const includedVersions = comparison.includedVersions || [comparison.current, comparison.previous, ...(comparison.older || [])].filter(Boolean);
  const previousItems = includedVersions.slice(1);
  const pairs = Array.isArray(comparison.pairs) ? comparison.pairs : [];
  const addedItems = Array.isArray(comparison.diff?.added) ? comparison.diff.added : EMPTY_LIST;
  const removedItems = Array.isArray(comparison.diff?.removed) ? comparison.diff.removed : EMPTY_LIST;
  const changedTerms = Array.isArray(comparison.diff?.changedTerms) ? comparison.diff.changedTerms : EMPTY_LIST;
  const diffStats = comparison.diff?.stats || {};
  const addedCount = diffStats.addedLines ?? addedItems.length;
  const removedCount = diffStats.removedLines ?? removedItems.length;
  const addedCountLabel = formatCompactNumber(addedCount) || '0';
  const removedCountLabel = formatCompactNumber(removedCount) || '0';
  const aiSummary = comparison.diff?.ai || null;
  const baselineDocument = comparison.baselineDocument || null;
  const baselineAi = comparison.baselineAi || null;
  const currentAi = comparison.currentAi || null; // Contexto inteligente da Nota Técnica atual
  const trackedVersions = comparison.versionsCount || previousItems.length + 1;

  // Estado para controlar a aba ativa do diff redesenhado
  const [activeTab, setActiveTab] = useState('diff'); // 'diff' | 'terms' | 'added' | 'removed'

  // Filtra dinamicamente as alterações de acordo com a pesquisa digitada
  const filteredAdded = useMemo(() => {
    if (!localSearchTerm) return addedItems;
    return addedItems.filter((item) =>
      String(item).toLowerCase().includes(localSearchTerm.toLowerCase())
    );
  }, [addedItems, localSearchTerm]);

  const filteredRemoved = useMemo(() => {
    if (!localSearchTerm) return removedItems;
    return removedItems.filter((item) =>
      String(item).toLowerCase().includes(localSearchTerm.toLowerCase())
    );
  }, [removedItems, localSearchTerm]);

  const filteredTerms = useMemo(() => {
    if (!localSearchTerm) return changedTerms;
    return changedTerms.filter((term) =>
      String(term).toLowerCase().includes(localSearchTerm.toLowerCase())
    );
  }, [changedTerms, localSearchTerm]);

  return (
    <div className="space-y-4">
      <AiDisclaimer />

      {/* 1. Análise da Nota Técnica Inicial (Baseline) */}
      <BaselineAnalysisCard baselineDocument={baselineDocument} ai={baselineAi} />

      {/* 2. Contexto Inteligente da Nota Técnica de Referência Atual (Inserido antes da comparação por IA) */}
      <DocumentContextCard currentDocument={comparison.current} ai={currentAi} />

      {/* 3. Relatório Comparativo de IA (Gemini Analisado) */}
      <AiAnalysisCard ai={aiSummary} />

      {/* 4. Resumo da Série Fiscal e Faixa de Versões */}
      <CompactCard tone="slate" className="bg-white dark:bg-gray-800">
        <SectionHeader eyebrow="Série monitorada" title={comparison.seriesKey} icon={GitCompareArrows}>
          <div className="grid min-w-full grid-cols-2 gap-2 sm:min-w-[24rem] sm:grid-cols-4">
            <SummaryMetric label="Mapeadas" value={trackedVersions} tone="slate" compact />
            <SummaryMetric label="Na faixa" value={includedVersions.length} tone="slate" compact />
            <SummaryMetric label="Adicionados" value={addedCountLabel} tone="emerald" compact />
            <SummaryMetric label="Removidos" value={removedCountLabel} tone="rose" compact />
          </div>
        </SectionHeader>

        <div className="mt-3 grid gap-3 2xl:grid-cols-[minmax(0,1.15fr)_minmax(18rem,0.85fr)]">
          <DocumentMiniCard
            item={comparison.current}
            label="Mais nova selecionada"
            tone="emerald"
            showDescription
          />

          <div className="rounded-2xl border border-gray-200 bg-gray-50/70 p-3 dark:border-gray-700 dark:bg-gray-900/20">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <History className="h-3.5 w-3.5 text-gray-500 dark:text-gray-300" />
                <p className="text-[9px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Incluídas na análise
                </p>
              </div>
              <CountBadge tone="slate">{previousItems.length}</CountBadge>
            </div>

            <div className="mt-2 space-y-2 2xl:max-h-72 2xl:overflow-y-auto 2xl:pr-1">
              {previousItems.map((item, index) => (
                <DocumentMiniCard
                  key={`${item.url}-${index}`}
                  item={item}
                  label={`Versão ${index + 1}`}
                  tone="slate"
                />
              ))}
            </div>
          </div>
        </div>
      </CompactCard>

      {/* 5. Painel Redesenhado em Abas de Alterações Objetivas */}
      <CompactCard tone="slate" className="bg-white dark:bg-gray-800">
        <div className="border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3 pb-2.5">
          <div>
            <span className="text-[9px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-500 block">Diferenças locais</span>
            <h4 className="text-sm font-black text-gray-800 dark:text-white uppercase tracking-tight">Alterações Objetivas</h4>
          </div>
          
          <div className="flex flex-wrap gap-1">
            <button
              onClick={() => setActiveTab('diff')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                activeTab === 'diff'
                  ? 'bg-sky-600 text-white shadow-sm'
                  : 'bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <GitCompare className="h-3.5 w-3.5" />
              Git Diff
            </button>
            <button
              onClick={() => setActiveTab('terms')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                activeTab === 'terms'
                  ? 'bg-sky-600 text-white shadow-sm'
                  : 'bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <Sparkles className="h-3.5 w-3.5" />
              Termos ({changedTerms.length})
            </button>
            <button
              onClick={() => setActiveTab('added')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                activeTab === 'added'
                  ? 'bg-emerald-600 text-white shadow-sm'
                  : 'bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <span className="font-black text-xs">+</span>
              Incluídos ({addedItems.length})
            </button>
            <button
              onClick={() => setActiveTab('removed')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                activeTab === 'removed'
                  ? 'bg-rose-600 text-white shadow-sm'
                  : 'bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <span className="font-black text-xs">-</span>
              Excluídos ({removedItems.length})
            </button>
          </div>
        </div>

        {/* Barra de Pesquisa Local Dinâmica e Estilizada */}
        <div className="mt-3 relative">
          <input
            type="text"
            placeholder="Pesquisar cláusulas, termos ou linhas do diff..."
            value={localSearchTerm}
            onChange={(e) => setLocalSearchTerm(e.target.value)}
            className="w-full h-9 pl-9 pr-14 rounded-xl border border-gray-200 bg-gray-50/50 text-xs font-bold text-gray-700 focus:border-sky-500 focus:bg-white focus:outline-none dark:border-gray-700 dark:bg-gray-900/60 dark:text-gray-200 dark:focus:border-sky-500 dark:focus:bg-gray-900 transition-all placeholder-gray-400"
          />
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
          {localSearchTerm && (
            <button
              type="button"
              onClick={() => setLocalSearchTerm('')}
              className="absolute right-3 top-2 text-[10px] font-black uppercase tracking-wider text-sky-600 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300 transition-colors"
            >
              Limpar
            </button>
          )}
        </div>

        <div className="mt-3.5">
          {activeTab === 'diff' && (
            <GitDiffViewer diffPreview={comparison.diff?.diffPreview} searchTerm={localSearchTerm} />
          )}

          {activeTab === 'terms' && (
            <div className="rounded-xl border border-sky-100 bg-sky-50/15 p-4 dark:border-sky-950/30 dark:bg-sky-950/5">
              <div className="flex items-center justify-between border-b border-sky-100/50 pb-2.5 mb-3 dark:border-sky-900/20">
                <span className="text-[10px] font-black uppercase tracking-widest text-sky-700 dark:text-sky-300">
                  Termos Técnicos Fiscais Alterados ou Mapeados
                </span>
                <span className="rounded-full bg-sky-100 dark:bg-sky-950/40 px-2 py-0.5 text-[9px] font-black text-sky-700 dark:text-sky-300 border border-sky-200/30">
                  {filteredTerms.length} termos
                </span>
              </div>
              
              {filteredTerms.length ? (
                <div className="flex flex-wrap gap-2">
                  {filteredTerms.map((token) => (
                    <span
                      key={`${comparison.seriesKey}-${token}`}
                      className="inline-flex items-center gap-1 rounded-full border border-sky-200/60 bg-white px-3 py-1.5 text-xs font-bold text-sky-700 dark:border-sky-800 dark:bg-gray-800 dark:text-sky-300 shadow-sm hover:scale-[1.02] transition-transform cursor-default"
                    >
                      <Sparkles className="h-3 w-3 text-sky-500" />
                      {token}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400 py-4 w-full text-center">
                  Nenhum termo novo relevante detectado{localSearchTerm ? ' correspondente à busca' : ''}.
                </p>
              )}
            </div>
          )}

          {activeTab === 'added' && (
            <div className="space-y-2.5">
              <div className="flex items-center justify-between border-b border-gray-100 pb-2 dark:border-gray-800">
                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-400">
                  Trechos e Cláusulas Adicionadas
                </span>
                <span className="rounded-full bg-emerald-100 dark:bg-emerald-950/40 px-2 py-0.5 text-[9px] font-black text-emerald-700 dark:text-emerald-400 border border-emerald-200/30">
                  {filteredAdded.length} itens
                </span>
              </div>
              {filteredAdded.length ? (
                <div className="grid gap-2.5">
                  {filteredAdded.map((item, idx) => (
                    <ClauseCard key={`added-${idx}`} text={item} type="added" />
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/30 px-3 py-6 text-center text-xs leading-5 text-gray-500 dark:border-gray-700 dark:bg-gray-900/10">
                  Nenhuma cláusula nova encontrada{localSearchTerm ? ' correspondente à busca' : ''}.
                </div>
              )}
            </div>
          )}

          {activeTab === 'removed' && (
            <div className="space-y-2.5">
              <div className="flex items-center justify-between border-b border-gray-100 pb-2 dark:border-gray-800">
                <span className="text-[10px] font-black uppercase tracking-widest text-rose-700 dark:text-rose-400">
                  Trechos e Cláusulas Removidas
                </span>
                <span className="rounded-full bg-rose-100 dark:bg-rose-950/40 px-2 py-0.5 text-[9px] font-black text-rose-700 dark:text-rose-400 border border-rose-200/30">
                  {filteredRemoved.length} itens
                </span>
              </div>
              {filteredRemoved.length ? (
                <div className="grid gap-2.5">
                  {filteredRemoved.map((item, idx) => (
                    <ClauseCard key={`removed-${idx}`} text={item} type="removed" />
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/30 px-3 py-6 text-center text-xs leading-5 text-gray-500 dark:border-gray-700 dark:bg-gray-900/10">
                  Nenhuma cláusula antiga removida encontrada{localSearchTerm ? ' correspondente à busca' : ''}.
                </div>
              )}
            </div>
          )}
        </div>
      </CompactCard>

      <TimelineComparisonSection versions={includedVersions} pairs={pairs} />
    </div>
  );
}

// Função auxiliar para formatar o tamanho dos arquivos em formato legível (KB/MB)
function formatBytes(bytes) {
  if (!bytes || isNaN(bytes)) return 'Indeterminado';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function buildComparisonDownloadUrl({ comparison, type, getApiUrl, buildAuthenticatedGetUrl }) {
  const currentId = comparison?.current?.id;
  const previousId = comparison?.previous?.id;

  if (!currentId || !previousId || typeof getApiUrl !== 'function') {
    return '';
  }

  const params = new URLSearchParams({
    type,
    newerDocumentId: String(currentId),
    olderDocumentId: String(previousId)
  });
  const rawUrl = `${getApiUrl('fiscal-document-downloads')}?${params.toString()}`;

  return typeof buildAuthenticatedGetUrl === 'function'
    ? buildAuthenticatedGetUrl(rawUrl)
    : rawUrl;
}

function DownloadLinkButton({ href, label, title, disabled = false, compact = false }) {
  const className = compact
    ? 'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-2 py-1 text-[9px] font-black uppercase tracking-wider text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
    : 'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl border border-sky-200 bg-white px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-sky-700 transition-colors hover:bg-sky-50 dark:border-sky-900/50 dark:bg-gray-900 dark:text-sky-300 dark:hover:bg-sky-950/20';

  if (disabled || !href) {
    return (
      <button
        type="button"
        disabled
        className={`${className} cursor-not-allowed opacity-50`}
        title={title}
      >
        <FileDown className="h-3 w-3" />
        {label}
      </button>
    );
  }

  return (
    <a
      href={href}
      className={className}
      title={title}
    >
      <FileDown className="h-3 w-3" />
      {label}
    </a>
  );
}

// Componente do pipeline de processamento em 5 etapas transparentes
function PipelineStepper({
  comparison,
  onGenerateAi,
  onRefreshAi,
  onForceRefreshDocs,
  getApiUrl,
  buildAuthenticatedGetUrl,
  isBusy = false
}) {
  const current = comparison?.current;
  const previous = comparison?.previous;
  const diff = comparison?.diff;
  const downloadUrls = useMemo(() => ({
    pdfs: buildComparisonDownloadUrl({ comparison, type: 'pdfs', getApiUrl, buildAuthenticatedGetUrl }),
    txts: buildComparisonDownloadUrl({ comparison, type: 'txts', getApiUrl, buildAuthenticatedGetUrl }),
    diff: buildComparisonDownloadUrl({ comparison, type: 'diff', getApiUrl, buildAuthenticatedGetUrl }),
    all: buildComparisonDownloadUrl({ comparison, type: 'all', getApiUrl, buildAuthenticatedGetUrl })
  }), [comparison, getApiUrl, buildAuthenticatedGetUrl]);
  
  // Status reais baseados no estado das propriedades dos documentos
  const hasCurrentPdf = current?.sourceByteLength > 0 || current?.extractedContentType?.toLowerCase().includes('pdf');
  const hasPreviousPdf = previous?.sourceByteLength > 0 || previous?.extractedContentType?.toLowerCase().includes('pdf');
  const step1Done = hasCurrentPdf && hasPreviousPdf;

  const step2Done = (current?.extractedTextLength > 0) && (previous?.extractedTextLength > 0);
  const step3Done = step2Done && diff?.hasChanges !== undefined;
  const step4Done = step3Done && Array.isArray(diff?.added);

  const hasBaselineAi = Boolean(comparison?.baselineAi?.summary);
  const hasRangeAi = Boolean(diff?.ai?.summary);
  const step5Done = hasBaselineAi && hasRangeAi;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white/80 p-4 shadow-md backdrop-blur-md dark:border-gray-700 dark:bg-gray-800/80">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-3 dark:border-gray-700/60">
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-sky-600 dark:text-sky-400">Pipeline de Processamento</p>
          <h4 className="text-base font-black text-gray-900 dark:text-white uppercase tracking-tight">Status das Etapas do Comparador</h4>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <DownloadLinkButton
            href={downloadUrls.all}
            label="Baixar tudo ZIP"
            title="Baixar PDFs originais, textos extraídos e diff em um único arquivo ZIP."
            disabled={!downloadUrls.all}
          />
          {onForceRefreshDocs && (
            <button
              type="button"
              onClick={onForceRefreshDocs}
              disabled={isBusy}
              className="inline-flex items-center gap-1.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-amber-700 transition-all hover:bg-amber-100 disabled:opacity-50 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-300"
              title="Baixar os arquivos originais do portal fiscal do SEFAZ novamente e reprocessar o texto limpo."
            >
              <RefreshCw className={`h-3 w-3 ${isBusy ? 'animate-spin' : ''}`} />
              Recarregar PDFs/TXTs
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 grid gap-3 grid-cols-1 md:grid-cols-5">
        {/* ETAPA 1: Baixando PDFs */}
        <div className={`rounded-xl border p-3 flex flex-col justify-between transition-all ${
          step1Done 
            ? 'border-emerald-200 bg-emerald-50/30 dark:border-emerald-900/30 dark:bg-emerald-950/10' 
            : 'border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-900/30'
        }`}>
          <div>
            <div className="flex items-center justify-between">
              <FileDown className={`h-5 w-5 ${step1Done ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`} />
              <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full border ${
                step1Done 
                  ? 'border-emerald-200 bg-emerald-100 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' 
                  : 'border-gray-200 bg-gray-100 text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400'
              }`}>
                {step1Done ? 'Em Cache' : 'Pendente'}
              </span>
            </div>
            <h5 className="mt-2.5 text-xs font-black text-gray-800 dark:text-white uppercase tracking-wider">1. Baixando PDFs</h5>
            <p className="mt-1 text-[10px] leading-relaxed text-gray-500 dark:text-gray-400">
              Download das versões nova e antiga diretamente do portal fiscal.
            </p>
          </div>
          <div className="mt-3 flex items-end justify-between gap-2 border-t border-gray-100 pt-2 dark:border-gray-700/50">
            <p className="text-[9px] font-bold text-gray-400 dark:text-gray-500">
              Novo: {formatBytes(current?.sourceByteLength)} <br />
              Antigo: {formatBytes(previous?.sourceByteLength)}
            </p>
            <DownloadLinkButton
              href={downloadUrls.pdfs}
              label="Baixar PDFs"
              title="Baixar os PDFs originais da versão nova e antiga em ZIP."
              disabled={!downloadUrls.pdfs}
              compact
            />
          </div>
        </div>

        {/* ETAPA 2: Convertendo PDFs em Texto */}
        <div className={`rounded-xl border p-3 flex flex-col justify-between transition-all ${
          step2Done 
            ? 'border-emerald-200 bg-emerald-50/30 dark:border-emerald-900/30 dark:bg-emerald-950/10' 
            : 'border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-900/30'
        }`}>
          <div>
            <div className="flex items-center justify-between">
              <FileText className={`h-5 w-5 ${step2Done ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`} />
              <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full border ${
                step2Done 
                  ? 'border-emerald-200 bg-emerald-100 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' 
                  : 'border-gray-200 bg-gray-100 text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400'
              }`}>
                {step2Done ? 'Extraído' : 'Pendente'}
              </span>
            </div>
            <h5 className="mt-2.5 text-xs font-black text-gray-800 dark:text-white uppercase tracking-wider">2. Convertendo PDFs em Texto</h5>
            <p className="mt-1 text-[10px] leading-relaxed text-gray-500 dark:text-gray-400">
              Extração do texto integral com limpeza de ruídos, paginação e sumários.
            </p>
          </div>
          <div className="mt-3 flex items-end justify-between gap-2 border-t border-gray-100 pt-2 dark:border-gray-700/50">
            <p className="text-[9px] font-bold text-gray-400 dark:text-gray-500">
              Novo: {current?.extractedTextLength ? `${current.extractedTextLength.toLocaleString('pt-BR')} ch` : '0 ch'} | {current?.extractedPageCount ? `${current.extractedPageCount} p.` : ''} <br />
              Antigo: {previous?.extractedTextLength ? `${previous.extractedTextLength.toLocaleString('pt-BR')} ch` : '0 ch'} | {previous?.extractedPageCount ? `${previous.extractedPageCount} p.` : ''}
            </p>
            <DownloadLinkButton
              href={downloadUrls.txts}
              label="Baixar TXTs"
              title="Baixar os textos extraídos da versão nova e antiga em ZIP."
              disabled={!step2Done || !downloadUrls.txts}
              compact
            />
          </div>
        </div>

        {/* ETAPA 3: Gerando Diferenças */}
        <div className={`rounded-xl border p-3 flex flex-col justify-between transition-all ${
          step3Done 
            ? 'border-emerald-200 bg-emerald-50/30 dark:border-emerald-900/30 dark:bg-emerald-950/10' 
            : 'border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-900/30'
        }`}>
          <div>
            <div className="flex items-center justify-between">
              <GitCompare className={`h-5 w-5 ${step3Done ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`} />
              <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full border ${
                step3Done 
                  ? 'border-emerald-200 bg-emerald-100 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' 
                  : 'border-gray-200 bg-gray-100 text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400'
              }`}>
                {step3Done ? 'Concluído' : 'Pendente'}
              </span>
            </div>
            <h5 className="mt-2.5 text-xs font-black text-gray-800 dark:text-white uppercase tracking-wider">3. Gerando Diferenças</h5>
            <p className="mt-1 text-[10px] leading-relaxed text-gray-500 dark:text-gray-400">
              Comparação intercalada das linhas adicionadas e excluídas.
            </p>
          </div>
          <div className="mt-3 flex items-end justify-between gap-2 border-t border-gray-100 pt-2 dark:border-gray-700/50">
            <p className="text-[9px] font-bold text-gray-400 dark:text-gray-500">
              Diff textual pronto para auditoria.
            </p>
            <DownloadLinkButton
              href={downloadUrls.diff}
              label="Baixar Diff"
              title="Baixar o diff textual em TXT."
              disabled={!step3Done || !downloadUrls.diff}
              compact
            />
          </div>
        </div>

        {/* ETAPA 4: Analisando Diferenças */}
        <div className={`rounded-xl border p-3 flex flex-col justify-between transition-all ${
          step4Done 
            ? 'border-emerald-200 bg-emerald-50/30 dark:border-emerald-900/30 dark:bg-emerald-950/10' 
            : 'border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-900/30'
        }`}>
          <div>
            <div className="flex items-center justify-between">
              <Eye className={`h-5 w-5 ${step4Done ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`} />
              <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full border ${
                step4Done 
                  ? 'border-emerald-200 bg-emerald-100 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' 
                  : 'border-gray-200 bg-gray-100 text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400'
              }`}>
                {step4Done ? 'Pronto' : 'Pendente'}
              </span>
            </div>
            <h5 className="mt-2.5 text-xs font-black text-gray-800 dark:text-white uppercase tracking-wider">4. Analisando Diferenças</h5>
            <p className="mt-1 text-[10px] leading-relaxed text-gray-500 dark:text-gray-400">
              Organização das mudanças estruturais, termos técnicos e regras afetadas.
            </p>
          </div>
          <div className="mt-3 border-t border-gray-100 pt-2 dark:border-gray-700/50">
            <p className="text-[9px] font-bold text-gray-400 dark:text-gray-500">
              +{diff?.added?.length || 0} adições | -{diff?.removed?.length || 0} remoções <br />
              {diff?.changedTerms?.length || 0} termos modificados.
            </p>
          </div>
        </div>

        {/* ETAPA 5: Enviando para IA */}
        <div className={`rounded-xl border p-3 flex flex-col justify-between transition-all ${
          step5Done 
            ? 'border-sky-200 bg-sky-50/30 dark:border-sky-900/30 dark:bg-sky-950/10' 
            : 'border-purple-200 bg-purple-50/10 dark:border-purple-900/10 dark:bg-purple-950/5 animate-pulse'
        }`}>
          <div>
            <div className="flex items-center justify-between">
              <Sparkles className={`h-5 w-5 ${step5Done ? 'text-sky-600 dark:text-sky-400' : 'text-purple-600 animate-spin-slow'}`} />
              <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full border ${
                step5Done 
                  ? 'border-sky-200 bg-sky-100 text-sky-800 dark:border-sky-800 dark:bg-sky-900/30 dark:text-sky-300' 
                  : 'border-purple-200 bg-purple-100 text-purple-800 dark:border-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
              }`}>
                {step5Done ? 'Em Cache' : 'Requer IA'}
              </span>
            </div>
            <h5 className="mt-2.5 text-xs font-black text-gray-800 dark:text-white uppercase tracking-wider font-semibold">5. Enviando para IA</h5>
            <p className="mt-1 text-[10px] leading-relaxed text-gray-500 dark:text-gray-400">
              Parecer normativo de impacto técnico gerado pelo Gemini.
            </p>
          </div>
          <div className="mt-3 border-t border-gray-100 pt-2 flex items-center justify-between dark:border-gray-700/50">
            <p className="text-[9px] font-bold text-gray-400 dark:text-gray-500 truncate mr-1">
              {step5Done 
                ? `Modelo: ${comparison?.baselineAi?.model || 'Gemini 2.5'}` 
                : 'Processamento lento.'
              }
            </p>
            {step5Done ? (
              <button
                type="button"
                onClick={onRefreshAi}
                disabled={isBusy}
                className="rounded-lg p-1 text-sky-700 hover:bg-sky-100 disabled:opacity-50 dark:text-sky-300 dark:hover:bg-gray-700"
                title="Refazer análise por IA (forçar atualização)."
              >
                <RefreshCw className="h-3 w-3" />
              </button>
            ) : (
              <button
                type="button"
                onClick={onGenerateAi}
                disabled={isBusy}
                className="rounded-lg bg-purple-600 px-2 py-1 text-[9px] font-black uppercase text-white hover:bg-purple-700 disabled:opacity-50"
                title="Enviar dados e diff normativa para a IA processar."
              >
                Gerar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DocumentComparatorPanel({
  comparisons = [],
  sourceDocument, // Documento que originou a comparação
  availableVersions = [],
  error,
  isFetching,
  aiAction,
  onBack,
  onCompareRange,
  onStartComparison,
  onGenerateAi,
  onRefreshAi,
  onForceRefreshDocs, // Nova prop para forçar redownload e processamento
  getApiUrl,
  buildAuthenticatedGetUrl,
  onRetry
}) {
  const [loadingMs, setLoadingMs] = useState(0);
  const [selectedOlderId, setSelectedOlderId] = useState('');
  const comparisonsList = useMemo(
    () => (Array.isArray(comparisons) ? comparisons : EMPTY_LIST),
    [comparisons]
  );

  const olderVersions = useMemo(() => {
    const sourceId = sourceDocument?.id ? String(sourceDocument.id) : '';
    const sortedVersions = sortVersionsByRecency(availableVersions);
    const sourceIndex = sortedVersions.findIndex((item) => String(item?.id) === sourceId);

    if (sourceIndex >= 0) {
      return sortedVersions.slice(sourceIndex + 1);
    }

    const sourceTime = parseVersionTime(sourceDocument);
    return sortedVersions.filter((item) => {
      if (sourceId && String(item?.id) === sourceId) return false;
      if (!sourceTime) return true;
      return parseVersionTime(item) <= sourceTime;
    });
  }, [availableVersions, sourceDocument]);

  useEffect(() => {
    if (!olderVersions.length) {
      setSelectedOlderId('');
      return;
    }

    setSelectedOlderId((current) => (
      olderVersions.some((item) => String(item?.id) === String(current))
        ? current
        : String(olderVersions[0].id)
    ));
  }, [olderVersions]);

  // Efeito para cronômetro de alta precisão atualizando a cada 100ms
  useEffect(() => {
    if (!isFetching) {
      setLoadingMs(0);
      return undefined;
    }

    const start = Date.now();
    const timer = setInterval(() => {
      setLoadingMs(Date.now() - start);
    }, 100);

    return () => {
      clearInterval(timer);
    };
  }, [isFetching]);

  const selectedComparison = useMemo(
    () => comparisonsList[0] || null,
    [comparisonsList]
  );

  // Mapeamos os documentos selecionados da faixa atual (se houver alteração de faixa)
  // Isso nos ajuda a rastrear com precisão o estado do cache físico
  const selectedNewerDoc = useMemo(() => {
    if (!selectedComparison) return null;
    return selectedComparison.current;
  }, [selectedComparison]);

  const selectedOlderDoc = useMemo(() => {
    if (!selectedComparison) return null;
    return selectedComparison.previous;
  }, [selectedComparison]);

  // Lógica transparente para determinar a real disponibilidade do cache local de cada etapa.
  // Evita exibir loaders demorados de forma síncrona para etapas instantâneas que já estão persistidas no banco.
  const cacheStatus = useMemo(() => {
    // Se a ação for recarregar documentos, forçamos o reprocessamento de tudo do zero
    if (aiAction === 'refresh_docs') {
      return {
        step1: false,
        step2: false,
        step3: false,
        step4: false,
        step5: false
      };
    }

    // Se o usuário solicitou apenas a geração/atualização da análise com IA, as etapas físicas 1 a 4 estão prontas
    if (aiAction === 'generate' || aiAction === 'refresh') {
      return {
        step1: true,
        step2: true,
        step3: true,
        step4: true,
        step5: false
      };
    }

    // Para o primeiro carregamento ou mudança de faixa padrão ('idle'):
    // 1. Sem dados carregados ainda: olhamos para o documento de origem (se ele tem PDF/TXT, pelo menos metade está em cache)
    if (!selectedComparison) {
      const hasSourcePdf = sourceDocument?.sourceByteLength > 0 || sourceDocument?.extractedContentType?.toLowerCase().includes('pdf');
      const hasSourceTxt = sourceDocument?.extractedTextLength > 0;
      return {
        step1: hasSourcePdf,
        step2: hasSourceTxt,
        step3: false,
        step4: false,
        step5: false
      };
    }

    // 2. Com comparisons já existentes: verificamos se os documentos selecionados possuem metadados físicos extraídos
    const newerPdf = selectedNewerDoc ? (selectedNewerDoc.sourceByteLength > 0) : (selectedComparison.current?.sourceByteLength > 0);
    const olderPdf = selectedOlderDoc ? (selectedOlderDoc.sourceByteLength > 0) : (selectedComparison.previous?.sourceByteLength > 0);
    const step1 = Boolean(newerPdf && olderPdf);

    const newerTxt = selectedNewerDoc ? (selectedNewerDoc.extractedTextLength > 0) : (selectedComparison.current?.extractedTextLength > 0);
    const olderTxt = selectedOlderDoc ? (selectedOlderDoc.extractedTextLength > 0) : (selectedComparison.previous?.extractedTextLength > 0);
    const step2 = Boolean(newerTxt && olderTxt);

    const step3 = step2; // Diff na memória é instantâneo se temos o texto
    const step4 = step3; // A análise do diff também é imediata

    const hasBaselineAi = Boolean(selectedComparison.baselineAi?.summary);
    const hasRangeAi = Boolean(selectedComparison.diff?.ai?.summary);
    const step5 = hasBaselineAi && hasRangeAi;

    return { step1, step2, step3, step4, step5 };
  }, [aiAction, selectedComparison, sourceDocument, selectedNewerDoc, selectedOlderDoc]);

  // Definição e estimativa de cada etapa no pipeline.
  // Permite organizar e calcular de forma transparente e linear o que está sendo processado.
  const stepsDetails = useMemo(() => {
    const elapsedSecs = loadingMs / 1000;
    
    // Tempos estimados de processamento para passos não cacheados (em segundos)
    const stepDurations = {
      0: 3.5, // Baixando PDFs
      1: 2.5, // Convertendo PDFs em Texto
      2: 0.8, // Gerando Diferenças
      3: 0.8, // Analisando Diferenças
      4: 10.0 // Enviando para IA
    };

    const details = [];
    let currentTimelineOffset = 0; // Controla o tempo de início cumulativo para etapas pendentes

    // Dados físicos para enriquecer as mensagens transparentes
    const currentDocSize = selectedComparison?.current?.sourceByteLength || sourceDocument?.sourceByteLength || 0;
    const prevDocSize = selectedComparison?.previous?.sourceByteLength || 0;
    const currentTextLength = selectedComparison?.current?.extractedTextLength || sourceDocument?.extractedTextLength || 0;
    const prevTextLength = selectedComparison?.previous?.extractedTextLength || 0;

    for (let i = 0; i < 5; i++) {
      const stepKey = `step${i + 1}`;
      const isCached = cacheStatus[stepKey];
      const title = LOADING_STEPS[i].title;
      const description = LOADING_STEPS[i].description;

      let status = 'waiting'; // 'waiting' | 'active' | 'done'
      let timerLabel = '';
      let progressVal = 0;

      if (isCached) {
        status = 'done';
        timerLabel = '✓ Concluído (Em Cache)';
        progressVal = 100;
      } else {
        const stepDuration = stepDurations[i];
        const stepStart = currentTimelineOffset;
        const stepEnd = currentTimelineOffset + stepDuration;

        if (elapsedSecs < stepStart) {
          status = 'waiting';
          timerLabel = '○ Aguardando...';
          progressVal = 0;
        } else if (elapsedSecs >= stepStart && elapsedSecs < stepEnd) {
          status = 'active';
          const elapsedInStep = elapsedSecs - stepStart;
          timerLabel = `⚡ Processando... ${elapsedInStep.toFixed(1)}s`;
          progressVal = (elapsedInStep / stepDuration) * 100;
        } else {
          status = 'done';
          timerLabel = `✓ Concluído em ${stepDuration.toFixed(1)}s`;
          progressVal = 100;
        }

        // Incrementa o offset da timeline acumulada apenas para os passos que de fato rodaram
        currentTimelineOffset += stepDuration;
      }

      // Adiciona mensagens contextuais e ultra-transparentes para cada etapa
      let contextualMessage = '';
      if (i === 0) {
        contextualMessage = isCached 
          ? `Arquivos locais prontos em cache (${formatBytes(currentDocSize)} e ${formatBytes(prevDocSize)})`
          : (status === 'active' ? 'Baixando PDFs originais no portal fiscal...' : 'Aguardando download dos PDFs...');
      } else if (i === 1) {
        contextualMessage = isCached
          ? `Textos extraídos (${formatCompactNumber(currentTextLength)} ch e ${formatCompactNumber(prevTextLength)} ch)`
          : (status === 'active' ? 'Convertendo PDFs em texto limpo...' : 'Aguardando conversão dos PDFs em texto...');
      } else if (i === 2) {
        contextualMessage = isCached
          ? 'Diferenças textuais geradas'
          : (status === 'active' ? 'Gerando diferenças intercaladas...' : 'Aguardando geração das diferenças...');
      } else if (i === 3) {
        contextualMessage = isCached
          ? 'Diferenças analisadas e termos identificados'
          : (status === 'active' ? 'Analisando diferenças, termos técnicos e regras...' : 'Aguardando análise das diferenças...');
      } else if (i === 4) {
        contextualMessage = isCached
          ? 'Relatório do Gemini carregado do cache local'
          : (status === 'active' ? 'Enviando contexto e diferenças para o Gemini...' : 'Aguardando envio para IA...');
      }

      details.push({
        index: i,
        title,
        description,
        status,
        timerLabel,
        progressVal,
        contextualMessage
      });
    }

    return details;
  }, [cacheStatus, loadingMs, selectedComparison, sourceDocument]);

  // Barra de progresso contínua calculada matematicamente a partir do avanço real das etapas ativas e concluídas
  const loadingProgressWidth = useMemo(() => {
    let totalProgress = 0;
    stepsDetails.forEach((step) => {
      if (step.status === 'done') {
        totalProgress += 20;
      } else if (step.status === 'active') {
        totalProgress += (step.progressVal / 100) * 20;
      }
    });
    // Limita em 98% para manter a barra fluida enquanto aguarda a resposta final do servidor
    return `${Math.min(totalProgress, 98)}%`;
  }, [stepsDetails]);

  // Define qual etapa de carregamento ativa exibir no cabeçalho principal
  const currentLoadingStep = useMemo(() => {
    const activeStep = stepsDetails.find((s) => s.status === 'active');
    if (activeStep) return activeStep;
    const waitingStep = stepsDetails.find((s) => s.status === 'waiting');
    if (waitingStep) return waitingStep;
    return stepsDetails[4]; // Fallback para o último
  }, [stepsDetails]);

  const loadingActionLabel = aiAction === 'refresh'
    ? 'Atualizando análises com IA'
    : aiAction === 'generate'
      ? 'Gerando análises com IA'
      : aiAction === 'refresh_docs'
        ? 'Recarregando documentos e textos do portal fiscal'
        : 'Processando comparação';

  if (isFetching) {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="border-b border-gray-100 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800/80 sm:px-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4 animate-spin text-sky-600 dark:text-sky-300" />
              <h3 className="text-sm font-black uppercase tracking-tight text-gray-800 dark:text-white">Comparador de documentos</h3>
              <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.25em] text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                Beta
              </span>
            </div>
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Voltar
              </button>
            ) : null}
          </div>
        </div>
        <div
          className="flex-1 overflow-y-auto p-3 sm:p-4"
          style={{ overscrollBehaviorY: 'contain', WebkitOverflowScrolling: 'touch' }}
        >
          {/* Card Detalhado dos Arquivos Físicos e Série em Análise no Pipeline */}
          <div className="mb-4 rounded-2xl border border-sky-100 bg-sky-50/20 p-4 backdrop-blur-md dark:border-sky-950/40 dark:bg-gray-900/30">
            <p className="text-[10px] font-black uppercase tracking-widest text-sky-600 dark:text-sky-400">Arquivos sendo processados no Pipeline</p>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white/60 p-3 dark:border-gray-700/50 dark:bg-gray-800/40">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300">
                  <FileText className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[9px] font-black uppercase tracking-wider text-gray-400 dark:text-gray-500">Documento Novo (Referência)</p>
                  <p className="mt-0.5 truncate text-xs font-bold text-gray-700 dark:text-gray-200" title={selectedComparison?.current?.nome || sourceDocument?.nome}>
                    {selectedComparison?.current?.nome || sourceDocument?.nome || 'Buscando arquivo recente...'}
                  </p>
                  {/* Informações detalhadas do arquivo */}
                  <p className="text-[9px] text-gray-400 dark:text-gray-500 mt-0.5">
                    {selectedComparison?.current?.sourceByteLength || sourceDocument?.sourceByteLength
                      ? `${formatBytes(selectedComparison?.current?.sourceByteLength || sourceDocument?.sourceByteLength)}`
                      : 'Tamanho pendente'} 
                    {selectedComparison?.current?.extractedPageCount || sourceDocument?.extractedPageCount
                      ? ` • ${selectedComparison?.current?.extractedPageCount || sourceDocument?.extractedPageCount} pág.`
                      : ''}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white/60 p-3 dark:border-gray-700/50 dark:bg-gray-800/40">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-300">
                  <History className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[9px] font-black uppercase tracking-wider text-gray-400 dark:text-gray-500">Documento Antigo (Histórico)</p>
                  <p className="mt-0.5 truncate text-xs font-bold text-gray-700 dark:text-gray-200" title={selectedComparison?.previous?.nome || selectedComparison?.older?.nome}>
                    {selectedComparison?.previous?.nome || selectedComparison?.older?.nome || 'Localizando arquivo anterior no SEFAZ...'}
                  </p>
                  {/* Informações detalhadas do arquivo */}
                  <p className="text-[9px] text-gray-400 dark:text-gray-500 mt-0.5">
                    {selectedComparison?.previous?.sourceByteLength || selectedComparison?.older?.sourceByteLength
                      ? `${formatBytes(selectedComparison?.previous?.sourceByteLength || selectedComparison?.older?.sourceByteLength)}`
                      : 'Tamanho pendente'}
                    {selectedComparison?.previous?.extractedPageCount || selectedComparison?.older?.extractedPageCount
                      ? ` • ${selectedComparison?.previous?.extractedPageCount || selectedComparison?.older?.extractedPageCount} pág.`
                      : ''}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-3 border-t border-gray-100 pt-2 flex items-center justify-between text-[10px] text-gray-400 dark:border-gray-700/40 dark:text-gray-500">
              <span>Série Técnica: <strong className="text-gray-600 dark:text-gray-400">{selectedComparison?.current?.seriesKey || sourceDocument?.seriesKey || 'Extraindo...'}</strong></span>
              <span>Portal SEFAZ: <strong className="text-gray-600 dark:text-gray-400 uppercase">{selectedComparison?.current?.portal || sourceDocument?.portal || 'Conectando...'}</strong></span>
            </div>
          </div>

          <div className="rounded-2xl border border-sky-200 bg-sky-50/70 p-4 dark:border-sky-900/40 dark:bg-sky-900/10">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-sky-600 dark:text-sky-300">
                  {loadingActionLabel}
                </p>
                <h4 className="mt-1 text-base font-black text-gray-900 dark:text-white">
                  {currentLoadingStep.title}
                </h4>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  {currentLoadingStep.description}
                </p>
              </div>
              <div className="rounded-full border border-sky-200 bg-white px-3 py-1 text-[11px] font-black text-sky-700 dark:border-sky-800 dark:bg-gray-800 dark:text-sky-300">
                {(loadingMs / 1000).toFixed(1)}s
              </div>
            </div>

            <div className="mt-4 h-2 overflow-hidden rounded-full bg-sky-100 dark:bg-sky-950/50">
              <div
                className="h-full rounded-full bg-sky-600 transition-all duration-300"
                style={{ width: loadingProgressWidth }}
              />
            </div>

            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              {stepsDetails.map((step, index) => {
                const isActive = step.status === 'active';
                const isDone = step.status === 'done';

                // Determina estilos específicos para cada estado do step
                let stepDetailColor = 'text-gray-400 dark:text-gray-500';
                if (isDone) {
                  stepDetailColor = 'text-emerald-600 dark:text-emerald-400 font-bold';
                } else if (isActive) {
                  stepDetailColor = 'text-sky-600 dark:text-sky-400 font-black animate-pulse';
                }

                return (
                  <div
                    key={step.title}
                    className={`rounded-xl border px-3 py-2.5 transition-all flex flex-col justify-between min-h-[6.5rem] ${
                      isActive
                        ? 'border-sky-300 bg-white dark:border-sky-700 dark:bg-gray-800 shadow-md scale-[1.02]'
                        : isDone
                          ? 'border-emerald-200 bg-emerald-50/70 dark:border-emerald-800 dark:bg-emerald-900/10'
                          : 'border-gray-200 bg-gray-50/70 dark:border-gray-700 dark:bg-gray-900/30'
                    }`}
                  >
                    <div>
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">
                          Etapa {index + 1}
                        </p>
                        {isDone && <span className="text-[10px] text-emerald-500 font-bold">✓</span>}
                        {isActive && <span className="text-[10px] text-sky-500 animate-spin">⚡</span>}
                      </div>
                      <p className="mt-0.5 text-xs font-bold text-gray-800 dark:text-gray-100">
                        {step.title}
                      </p>
                      {/* Mensagem contextual transparente e detalhada */}
                      <p className="mt-1 text-[9px] text-gray-500 dark:text-gray-400 leading-snug">
                        {step.contextualMessage}
                      </p>
                    </div>
                    <p className={`mt-2 text-[9px] uppercase tracking-wider ${stepDetailColor}`}>
                      {step.timerLabel}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-3 space-y-3">
            {[1, 2].map((index) => (
              <div key={index} className="h-40 animate-pulse rounded-2xl border border-gray-100 bg-gray-50/50 dark:border-gray-700/50 dark:bg-gray-800/30" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="border-b border-gray-100 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800/80 sm:px-5">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-black uppercase tracking-tight text-gray-800 dark:text-white">Comparador de documentos</h3>
              <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.25em] text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                Beta
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="rounded-full bg-sky-600 px-2.5 py-1 text-[10px] font-black text-white">
              {comparisonsList.length}
            </div>
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Voltar
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div
        className="flex-1 overflow-y-auto p-3 sm:p-4"
        style={{ overscrollBehaviorY: 'contain', WebkitOverflowScrolling: 'touch' }}
      >
        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 dark:border-red-900/40 dark:bg-red-900/10">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-300" />
              <div>
                <p className="text-sm font-bold text-red-700 dark:text-red-200">Não foi possível montar o comparador.</p>
                <p className="mt-1 text-xs text-red-600/80 dark:text-red-200/80">{error}</p>
                {onRetry ? (
                  <button
                    type="button"
                    onClick={onRetry}
                    className="mt-3 rounded-xl bg-red-600 px-3 py-2 text-[11px] font-black uppercase tracking-widest text-white transition-colors hover:bg-red-700"
                  >
                    Tentar novamente
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        ) : comparisonsList.length === 0 && !isFetching ? (
          <ComparisonSetupScreen
            sourceDocument={sourceDocument}
            olderVersions={olderVersions}
            selectedOlderId={selectedOlderId}
            onSelectOlderId={setSelectedOlderId}
            onStartComparison={onStartComparison}
            isFetching={isFetching}
          />
        ) : (
          <div className="space-y-3">
            {selectedComparison ? (
              <PipelineStepper
                comparison={selectedComparison}
                onGenerateAi={onGenerateAi}
                onRefreshAi={onRefreshAi}
                onForceRefreshDocs={onForceRefreshDocs}
                getApiUrl={getApiUrl}
                buildAuthenticatedGetUrl={buildAuthenticatedGetUrl}
                isBusy={isFetching}
              />
            ) : null}

            {selectedComparison ? (
              <ComparisonRangeSelector
                comparison={selectedComparison}
                onCompareRange={onCompareRange}
                disabled={isFetching}
              />
            ) : null}

            {selectedComparison ? (
              <ComparisonCard comparison={selectedComparison} />
            ) : (
              <div className="flex min-h-[14rem] items-center justify-center rounded-2xl border border-gray-200 bg-gray-50/70 dark:border-gray-700 dark:bg-gray-900/20">
                <p className="text-sm font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                  Nenhuma comparação disponível para exibição
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
